import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { FaEnvelope, FaExclamationTriangle } from "react-icons/fa";
import { axiosAdminAuthClient, axiosApiClient } from "../../api";
import { selectUser, setUser } from "../../store/slices/auth/slice";

import {
  AuthPageWrapper,
  AuthBackground,
  AuthWrapper,
  AuthContainer,
  AuthTitle,
  AuthSubtitle,
  AuthForm,
  AuthLink,
  FormGroup,
  Input,
  InputWrapper,
  IconLeft,
  StatusContainer,
  StatusIcon,
  StatusTitle,
  StatusMessage,
  EmailDisplay,
  ButtonGroup,
  VerificationButtonWrapper,
} from "./Auth.styles";

import Button from "../../components/Button/Button";
import SmallClouds from "../../components/utils/SmallClouds";
import { logout } from "../../store/slices/auth/asyncThunk";

export default function EmailVerificationNotice() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [isResending, setIsResending] = useState(false);
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isChanging, setIsChanging] = useState(false);

  const handleResendEmail = async () => {
    if (!user?.email) {
      toast.error("Nema email adrese");
      return;
    }

    setIsResending(true);
    try {
      const response = await axiosAdminAuthClient.post("/resend-verification", {
        email: user.email,
      });

      if (response.data.success) {
        toast.success(
          "Verifikacioni email je uspešno poslat! Proverite vaš inbox."
        );
      }
    } catch (error) {
      console.error("Resend email error:", error);

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Greška pri slanju verifikacionog email-a");
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();

    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      toast.error("Unesite novu email adresu");
      return;
    }

    setIsChanging(true);
    try {
      // axiosApiClient šalje Bearer token (axiosAdminAuthClient briše headere,
      // a change-email zahteva prijavljenog korisnika)
      const response = await axiosApiClient.post("auth/change-email", {
        email: trimmedEmail,
      });

      if (response.data.success) {
        const updatedEmail = response.data.data?.email || trimmedEmail;
        dispatch(
          setUser({ user: { ...user, email: updatedEmail }, rememberMe: true })
        );
        setShowChangeForm(false);
        setNewEmail("");
        toast.success(
          "Adresa je ispravljena! Verifikacioni link je poslat na novu adresu."
        );
      }
    } catch (error) {
      console.error("Change email error:", error);

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Greška pri promeni email adrese");
      }
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <AuthPageWrapper>
      <AuthBackground />
      <SmallClouds />

      <AuthWrapper>
        <AuthContainer>
          <AuthTitle>Verifikacija Email-a</AuthTitle>
          <AuthSubtitle>Potrebno je da verifikujete vaš email</AuthSubtitle>

          <AuthForm as="div">
            <StatusContainer>
              <StatusIcon $status="error">
                <FaExclamationTriangle />
              </StatusIcon>

              <StatusTitle>Email nije verifikovan</StatusTitle>

              <StatusMessage>
                Poslali smo verifikacioni mejl na adresu ispod. Kliknite na
                link iz mejla da potvrdite nalog. Ako mejl ne stiže, proverite
                spam folder ili pošaljite novi link.
              </StatusMessage>

              <EmailDisplay>
                Email: <strong>{user?.email}</strong>
              </EmailDisplay>

              {!showChangeForm && (
                <StatusMessage>
                  Nije vaša adresa?{" "}
                  <AuthLink onClick={() => setShowChangeForm(true)}>
                    Ispravite je
                  </AuthLink>
                </StatusMessage>
              )}

              {showChangeForm && (
                <form onSubmit={handleChangeEmail} style={{ width: "100%" }}>
                  <FormGroup>
                    <InputWrapper>
                      <Input
                        type="email"
                        name="newEmail"
                        placeholder="Ispravna email adresa"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        $hasLeftIcon
                      />
                      <IconLeft>
                        <FaEnvelope />
                      </IconLeft>
                    </InputWrapper>
                  </FormGroup>

                  <VerificationButtonWrapper>
                    <Button
                      type="submit"
                      disabled={isChanging}
                      loading={isChanging}
                      title={"Sačuvaj i pošalji link"}
                      lightColor={"var(--plava)"}
                      darkColor={"var(--bg-plava)"}
                    />
                  </VerificationButtonWrapper>

                  <ButtonGroup>
                    <AuthLink
                      onClick={() => {
                        setShowChangeForm(false);
                        setNewEmail("");
                      }}
                    >
                      Odustani
                    </AuthLink>
                  </ButtonGroup>
                </form>
              )}

              {!showChangeForm && (
                <VerificationButtonWrapper>
                  <Button
                    onClick={handleResendEmail}
                    disabled={isResending}
                    loading={isResending}
                    title={"Pošalji novi link"}
                    lightColor={"var(--plava)"}
                    darkColor={"var(--bg-plava)"}
                    leftIcon={<FaEnvelope />}
                  />
                </VerificationButtonWrapper>
              )}

              <ButtonGroup>
                <AuthLink
                  onClick={() =>
                    dispatch(logout()).then(() => {
                      navigate("/");
                    })
                  }
                >
                  Odjavi se
                </AuthLink>
                <AuthLink onClick={() => navigate("/")}>
                  Početna strana
                </AuthLink>
              </ButtonGroup>
            </StatusContainer>
          </AuthForm>
        </AuthContainer>
      </AuthWrapper>
    </AuthPageWrapper>
  );
}
